/**
 * RP4 — SceneContent ref setup helper extracted from `Scene3DStage.tsx`.
 *
 * Hosts the `buildPreview` callback factory that lifts a metadata-stub
 * id back to a full `EntityPreview` via the live geometryDocument's
 * dimension-style / block-record / block tables. The factory output is
 * memoised on `geometryDocument` identity so the cache-direct path
 * pays the per-id cost only on cache miss.
 *
 * DEFUSE S11 — also hosts `resolveEntity`, the typed twin: the same
 * Map → compact-cache resolution WITHOUT the `buildShellEntityPreview`
 * bag flatten. The typed-first render path
 * (`adapters/typedFirstRenderable.ts`) dispatches the resolved
 * `DrawingEntity` straight into `entityToRenderableTyped`; `buildPreview`
 * remains ONLY the lazy bag fallback for the not-yet-typed kinds
 * (`BAG_FALLBACK_RENDER_KINDS`).
 */

import { getEntityById } from "../../state/editorStore";
import { getEntityFromCompactCache } from "../../state/mutationStore";
import { buildShellEntityPreview } from "../../state/dxfGeometry";
import type {
  BlockDefinition,
  BlockRecord,
  DimensionStyle,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
import type { EntityPreview } from "@dxf-ai-editor/contracts";

interface GeometryDocumentLike {
  styleTables?: { dimensionStyles?: ReadonlyArray<DimensionStyle> };
  blockRecords?: Record<string, BlockRecord>;
  blocks?: Record<string, BlockDefinition>;
  // Drawing-level $INSUNITS — forwarded to buildShellEntityPreview so a
  // block insert's "Block Unit Factor" reflects the real unit-conversion
  // ratio. Optional (default 0/Unitless → factor 1).
  insunits?: number;
}

/**
 * Build a `buildPreview(id)` closure for the given live geometry
 * document. Pure — caller is responsible for memoising the result on
 * `geometryDocument` identity.
 */
export function buildPreviewCallbacks(
  geometryDocument: GeometryDocumentLike,
): {
  buildPreview: (id: string) => EntityPreview | null;
  resolveEntity: (id: string) => DrawingEntity | null;
} {
  const dimStylesByName = new Map<string, DimensionStyle>();
  for (const s of geometryDocument.styleTables?.dimensionStyles ?? []) {
    dimStylesByName.set(s.name, s);
  }
  const blockRecords: Record<string, BlockRecord> =
    geometryDocument.blockRecords ?? {};
  const blocks = geometryDocument.blocks ?? {};
  const drawingInsunits = geometryDocument.insunits ?? 0;
  // DEFUSE S11 — typed resolver for the typed-first render path. Same
  // Map → compact-cache ordering as `buildPreview` below (see its comment),
  // same defensive AttDef narrowing — minus the bag flatten.
  const resolveEntity = (id: string): DrawingEntity | null => {
    const entity = getEntityById(id) ?? getEntityFromCompactCache(id);
    if (!entity || entity.type === "Attdef") return null;
    return entity;
  };
  const buildPreview = (id: string): EntityPreview | null => {
    // post-V9 Section 0 Iter 6.A — read legacy Map FIRST (always fresh
    // post-mutation), fall back to compact cache for cold tail entities
    // never re-touched since import. Compact cache is populated only at
    // huge-import time (Iter 4) and is NOT updated by commitMutation /
    // applyDiffToCache yet (Iter 6.C will wire compact writes). Until
    // then, querying compact first would return stale data after any
    // move/grip-drag/property-apply mutation, propagating outdated
    // previews into cache-direct picking paths.
    //
    // `getEntityById` already encapsulates Map -> compact -> reactive doc
    // ordering (editorStore.ts Iter 6.A); the explicit second-arg
    // `getEntityFromCompactCache` is redundant today but kept defensively
    // for the post-Iter-6.D world.
    const entity = getEntityById(id) ?? getEntityFromCompactCache(id);
    if (!entity) return null;
    // Phase 3 followup (codex P2): `getEntityFromCompactCache` now widens
    // its return type to `DrawingEntity | AttdefEntity` (block-children
    // path needs AttDef resolvable through the cache). The renderer
    // does not understand AttDef previews — narrow here so
    // `buildShellEntityPreview` keeps its `DrawingEntity` contract.
    // In practice AttDef lives in `blocks[].children`, not in any space
    // the renderer walks, so this branch is defensive only.
    if (entity.type === "Attdef") return null;
    try {
      return buildShellEntityPreview(
        entity,
        dimStylesByName,
        blockRecords,
        blocks,
        drawingInsunits,
      );
    } catch {
      // Unsupported entity types (Ellipse, etc.) raise — we treat
      // them as "no preview" so the visible-set walker skips them.
      return null;
    }
  };
  return { buildPreview, resolveEntity };
}
